import type {
  ActionLabelConfig,
  CommandType,
  LiveStateView,
  ServiceAction,
  ServiceStatus,
} from '../types/ServiceManager.types';

/** Service name substrings that identify protected system services. */
export const SYSTEM_SERVICE_PATTERNS = [
  'FusionBackend',
  'EventService',
  'SupervisorService',
] as const;

/** Label text and Bootstrap variant for each service action modal. */
export const ACTION_LABELS: Record<ServiceAction, ActionLabelConfig> = {
  start: { label: 'Start', variant: 'success' },
  stop: { label: 'Stop', variant: 'danger' },
  restart: { label: 'Restart', variant: 'warning' },
};

/** Available command types for the history filter dropdown. */
export const COMMAND_OPTIONS: CommandType[] = [
  'start',
  'stop',
  'restart',
  'admin_start',
  'admin_stop',
];

/** Number of command history rows fetched from the API in a single request. */
export const PAGE_LIMIT = 50;

/** Number of command history rows displayed per page in the UI. */
export const HISTORY_ROWS_PER_PAGE = 5;

/** Delay in ms before refreshing the service list after an action. */
export const REFRESH_DELAY_MS = 1000;

/**
 * Services the supervisor reports but will never start or stop. It sends this
 * as `control: 'observe'` on each row; these patterns are only a fallback for a
 * supervisor that predates the field. Prefer the server's fact — a name pattern
 * stops being true the day something is renamed.
 */
export const RESTART_ONLY_SERVICE_PATTERNS = ['FusionBackend', 'EventService'] as const;

/**
 * Fallbacks for the two timing windows a toggle response quotes, used when an
 * older supervisor omits them and, for the disable confirmation, before any
 * response exists. Both are the supervisor's tick interval.
 *
 * Kept as two constants on purpose: `appliesWithinMs` comes back as 0 on a
 * disable, because the process is stopped inside the request, while
 * `reapWithinMs` stays a full tick — the reap is the backstop that re-stops
 * anything which starts a disabled service.
 */
export const DEFAULT_APPLIES_WITHIN_MS = 10_000;
export const DEFAULT_REAP_WITHIN_MS = 10_000;

/** Maximum length of the audit reason recorded with any service action. */
export const MAX_REASON_LENGTH = 500;

/**
 * How the live (PM2) column reads for each state the supervisor relays.
 *
 * `missing` and `unknown` are opposite facts and never share wording or colour:
 * `missing` means PM2 was queried and has no such process, so the rest of the
 * row is trustworthy; `unknown` means PM2 could not be reached at all, so
 * nothing in the live column can be trusted, including a `running: false`.
 */
export const LIVE_STATE_VIEW: Record<string, LiveStateView> = {
  online: { label: 'Running', hint: 'PM2 reports the process up.', variant: 'success' },
  stopped: {
    label: 'Stopped',
    hint: 'PM2 has the process registered and stopped.',
    variant: 'danger',
  },
  errored: {
    label: 'Errored',
    hint: 'PM2 reports the process errored. Check its logs.',
    variant: 'danger',
  },
  launching: { label: 'Launching', hint: 'PM2 is starting the process.', variant: 'warning' },
  stopping: { label: 'Stopping', hint: 'PM2 is stopping the process.', variant: 'warning' },
  missing: {
    label: 'Not in PM2',
    hint: 'PM2 was queried and has no such process. The rest of this row is trustworthy.',
    variant: 'secondary',
  },
  unknown: {
    label: 'PM2 unreachable',
    hint: 'PM2 could not be queried, so the live state is unknown. This is not the same as "not in PM2".',
    variant: 'warning',
  },
};

/**
 * How a Services-tab status reads, in the same vocabulary the Deployments
 * tab uses for live PM2 state (see LIVE_STATE_VIEW).
 *
 * Both tabs render these through the shared StatusDot, so "Running is green"
 * is defined once. Two copies would drift the moment one tab was themed.
 */
export const STATUS_VIEW: Record<ServiceStatus, LiveStateView> = {
  Running: { label: 'Running', hint: 'PM2 reports the process up.', variant: 'success' },
  Stopped: {
    label: 'Stopped',
    hint: 'PM2 has the process registered and stopped.',
    variant: 'danger',
  },
  Restarting: {
    label: 'Restarting',
    hint: 'A restart was requested and the process is cycling.',
    variant: 'warning',
  },
  Unknown: {
    label: 'Unknown',
    hint: 'The supervisor reported a state this UI has no wording for.',
    variant: 'secondary',
  },
};
